// ══════════════════════════════════════════════════════════════════════════════
//  Modal: la ventana de trabajo del canon (icono, titulo estable, subtitulo, aside, toolbar, cuerpo, pie).
// ══════════════════════════════════════════════════════════════════════════════
import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { C, T, R, E, SP, GLASS } from '../../design.js';
import { Icon } from './Icon.js';
import { useEscToClose, useEnterToAccept, useModalZ } from './hooks.js';

// Modal — slots opcionales (`footer`), z-index automático (useModalZ; el `zIndex`
//   explícito sigue ganando) y full-screen opt-in en móvil (`fullScreenMobile`,
//   vía clase gb-modal-fs). API previa intacta: title/children/onClose/width/scrollBody.
//
//   Cabecera de documento (opt-in, todo opcional): `icon` pinta la pastilla de
//   identidad a la izquierda —con el tono de `iconTone`, mismo lenguaje que el
//   ConfirmModal: primary informa, warning advierte, danger destruye—,
//   `subtitle` la segunda línea de contexto bajo el título y `aside` los
//   indicadores que pertenecen al documento (estado, folio) a la derecha, antes
//   de la ✕. Sin ellos la cabecera se dibuja exactamente igual que antes.
//   `flushBody` entrega el cuerpo a sangre (sin padding): es para modales cuyo
//   contenido ES una hoja —visores de PDF, lienzos—, donde el marco blanco solo
//   compite con el chrome propio del visor.
//   `toolbar` (ventana de trabajo): cinta compacta de herramientas entre la
//   cabecera y el cuerpo, pensada para los átomos de Cinta.js (BotonCinta,
//   MenuCinta, SepCinta). Las herramientas del documento viven aquí, nunca en
//   el pie: el pie es solo estado de cambios + Cancelar + UNA acción primaria.
//   `onRequestClose` (opt-in, 2026-10-01): la ✕ y Escape lo consultan antes de
//   cerrar; si devuelve false la ventana se queda, para que un formulario con
//   cambios pregunte en su pie si se descartan. Vive en un ref: `close` no
//   cambia y la pila de Escape no se reordena.
const MODAL_TILE = {
  primary: [C.primaryLight,  C.primary],
  warning: [C.warningLight,  C.warning],
  danger:  [C.criticalLight, C.critical],
  success: [C.successLight,  C.success],
};
const Modal = ({ title, subtitle, icon, iconTone="primary", aside, toolbar, children, footer, onClose, onAccept, onRequestClose, width=560, scrollBody=false, zIndex, fullScreenMobile=false, flushBody=false }) => {
  const [closing, setClosing] = useState(false);
  const pideCerrarRef = useRef(onRequestClose);
  pideCerrarRef.current = onRequestClose;
  const close = useCallback(() => {
    if (typeof pideCerrarRef.current === "function" && pideCerrarRef.current() === false) return;
    setClosing(true);
  }, []);
  useEscToClose(close);
  // Enter = acción primaria si el caller la declara (onAccept); durante la
  // animación de salida queda inerte para no re-disparar la acción.
  useEnterToAccept(closing ? null : onAccept);
  const z = useModalZ(zIndex);
  const scroll = scrollBody || !!footer;   // con footer el cuerpo scrollea y el pie queda fijo
  const [tileBg, tileFg] = MODAL_TILE[iconTone] || MODAL_TILE.primary;
  const done = e => { if (closing && e.animationName === "gbModalOut") onClose(); };   // (Obj. F) cierra tras la salida
  useEffect(() => { if (!closing) return; const t = setTimeout(onClose, 240); return () => clearTimeout(t); }, [closing, onClose]);   // fallback si no dispara animationend
  return createPortal(
    <div className={["gb-modal-scrim",closing&&"is-closing",fullScreenMobile&&"gb-modal-fs-wrap"].filter(Boolean).join(" ")} style={{position:"fixed",inset:0,...GLASS.scrim,zIndex:z,display:"flex",alignItems:scroll?"center":"flex-start",justifyContent:"center",padding:scroll?"24px 16px":"40px 16px",overflowY:scroll?"hidden":"auto"}}>
      <div onAnimationEnd={done} className={["gb-modal-panel",closing&&"is-closing",fullScreenMobile&&"gb-modal-fs"].filter(Boolean).join(" ")} style={{background:C.flotante,borderRadius:R.modal,width:"100%",maxWidth:width,boxShadow:E.e3,...(scroll?{display:"flex",flexDirection:"column",maxHeight:"calc(100vh - 48px)"}:{})}}>
        <div style={{display:"flex",alignItems:"center",gap:SP.md,padding:"18px 22px",borderBottom:`1px solid ${C.borderLight}`,flexShrink:0}}>
          {icon && (
            <div style={{width:34,height:34,borderRadius:R.md,background:tileBg,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
              <Icon name={icon} size={17} color={tileFg}/>
            </div>
          )}
          <div style={{minWidth:0,flex:1}}>
            <h3 style={{margin:0,...T.headingSm,color:C.text}}>{title}</h3>
            {subtitle && <div style={{...T.micro,color:C.textMuted,marginTop:2,minWidth:0}}>{subtitle}</div>}
          </div>
          {aside && <div style={{display:"flex",alignItems:"center",gap:SP.sm,flexShrink:0}}>{aside}</div>}
          <button onClick={close} aria-label="Cerrar" className="gb-ghost gb-press gb-i gb-focus" style={{background:"none",border:"none",cursor:"pointer",color:C.textMuted,fontSize:18,lineHeight:1,width:28,height:28,padding:0,display:"flex",alignItems:"center",justifyContent:"center",borderRadius:R.sm,flexShrink:0}}>✕</button>
        </div>
        {toolbar && (
          <div style={{display:"flex",alignItems:"stretch",flexWrap:"wrap",gap:2,padding:"6px 14px 5px",borderBottom:`1px solid ${C.borderLight}`,flexShrink:0}}>{toolbar}</div>
        )}
        <div style={{padding:flushBody?0:"20px 22px",...(scroll?(flushBody?{flex:1,minHeight:0,display:"flex",flexDirection:"column",overflow:"hidden"}:{overflowY:"auto",flex:1}):{})}}>{children}</div>
        {footer && <div style={{display:"flex",gap:10,justifyContent:"flex-end",alignItems:"center",padding:"14px 22px",borderTop:`1px solid ${C.borderLight}`,flexShrink:0}}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};





export { Modal };
